import useDebouncedAction from "@/hooks/useDebouncedAction";
import useMapMode from "@/services/store/useMapMode";
import { logger } from "@/utils/functions/logger";
import SearchIcon from "@mui/icons-material/Search";
import { Box, MenuItem, useTheme } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { HTMLAttributes, useState } from "react";

type SearchResult = {
  place_id: number;
  display_name: string;
  name: string;
  lat: string;
  lon: string;
};

const initialValue = {
  place_id: 0,
  display_name: "",
  name: "",
  lat: "0",
  lon: "0",
};

const SearchBar = () => {
  const theme: ThemeType = useTheme();
  const leafletMap = useMapMode((state) => state.map);

  const [selectedValue, setSelectedValue] = useState<SearchResult>(initialValue);
  const [options, setOptions] = useState<SearchResult[]>([]);

  const [inputValue, setInputValue] = useState("");

  const handleSearch = async () => {
    if (!inputValue || inputValue === selectedValue.display_name) return;
    try {
      const response = await fetch(`https://nominatim.openstreetmap.org/search?city=${inputValue}&format=json`);
      const data = await response.json();

      if (data && data.length > 0) setOptions(data);
    } catch (error) {
      logger.error("Error fetching coordinates:", error);
    }
  };

  useDebouncedAction([inputValue], handleSearch);

  const resetInput = () => {
    setInputValue("");
    setSelectedValue(initialValue);
  };

  const handleInputChange = (_: React.SyntheticEvent<Element, Event>, value: string, reason: string) => {
    setInputValue(value);
    if (reason === "reset") {
      resetInput();
    }
  };

  const handleOptionClick = (value: SearchResult) => () => {
    setSelectedValue(value);
    setInputValue(value.display_name);
    leafletMap.flyTo({ lat: parseFloat(value.lat), lng: parseFloat(value.lon) }, 16, { duration: 2 });
  };

  return (
    <Box
      sx={{
        "& fieldset": {
          color: theme.palette.primary.contrastText,
          borderColor: theme.palette.primary.contrastText,
        },
        "& label.Mui-focused": {
          color: theme.palette.primary.contrastText,
        },
        "& label": {
          color: theme.palette.disabled.main,
        },
        // focused color for input with variant='outlined'
        "& .MuiOutlinedInput-root": {
          "&.Mui-focused fieldset": {
            borderColor: theme.palette.primary.contrastText,
          },
          "&:hover fieldset": {
            borderColor: theme.palette.primary.contrastText,
          },
        },

        "& .MuiAutocomplete-clearIndicator": {
          color: theme.palette.primary.contrastText,
        },
      }}>
      <Autocomplete
        popupIcon={null}
        sx={{ width: "fit-content", minWidth: "400px" }}
        value={selectedValue}
        onChange={(_, value: SearchResult | null) => {
          if (value) setSelectedValue(value);
        }}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        options={options}
        getOptionLabel={(option: SearchResult) => option?.display_name}
        renderInput={(params) => (
          <TextField
            {...params}
            fullWidth
            sx={{ color: theme.palette.primary.contrastText, borderRadius: 3, ml: 1 }}
            inputProps={{
              ...params.inputProps,
              style: {
                color: theme.palette.primary.contrastText,
              },
            }}
            label="Recherchez une ville"
            variant="outlined"
            InputProps={{
              ...params.InputProps,
              startAdornment: <SearchIcon sx={{ color: theme.palette.primary.contrastText }} />,
            }}
          />
        )}
        renderOption={(_: HTMLAttributes<HTMLLIElement>, option: SearchResult) => (
          <MenuItem key={option.place_id} onClick={handleOptionClick(option)}>
            {option.display_name}
          </MenuItem>
        )}
      />
    </Box>
  );
};

export default SearchBar;
